import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { tenantApi, receiptApi } from '../services/api';
import { format, startOfMonth, endOfMonth } from 'date-fns';
import { ArrowUpTrayIcon, DocumentIcon, XMarkIcon } from '@heroicons/react/24/outline';

const ImportReceiptForm = () => {
  const { tenantId } = useParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(false);
  const [tenants, setTenants] = useState([]);
  const [selectedTenant, setSelectedTenant] = useState(null);
  const [file, setFile] = useState(null);
  const [dragging, setDragging] = useState(false);
  const fileInputRef = useRef(null);

  const today = new Date();
  const [fields, setFields] = useState({
    tenantId: tenantId || '',
    receiptNo: '',
    basicRent: '',
    charges: '',
    paymentDate: format(today, 'yyyy-MM-dd'),
    periodStart: format(startOfMonth(today), 'yyyy-MM-dd'),
    periodEnd: format(endOfMonth(today), 'yyyy-MM-dd'),
    paymentType: 'Bank Transfer',
    language: 'fr',
    isSigned: false,
    notes: '',
  });

  useEffect(() => {
    tenantApi.getAll()
      .then(res => {
        setTenants(res.data);
        if (tenantId) {
          const t = res.data.find(t => t.id === parseInt(tenantId));
          if (t) applyTenant(t);
        }
      })
      .catch(() => toast.error('Failed to load tenants'));
  }, [tenantId]);

  const applyTenant = (t) => {
    setSelectedTenant(t);
    setFields(f => ({
      ...f,
      tenantId: String(t.id),
      basicRent: String(t.basicRent ?? ''),
      charges: String(t.charges ?? ''),
    }));
  };

  const handleTenantChange = (e) => {
    const id = e.target.value;
    setFields(f => ({ ...f, tenantId: id }));
    const t = tenants.find(t => t.id === parseInt(id));
    if (t) applyTenant(t);
    else setSelectedTenant(null);
  };

  const handleField = (e) => {
    const { name, value, type, checked } = e.target;
    setFields(f => ({ ...f, [name]: type === 'checkbox' ? checked : value }));
  };

  const acceptFile = (f) => {
    if (!f) return;
    if (f.type !== 'application/pdf' && !f.name.toLowerCase().endsWith('.pdf')) {
      toast.error('Only PDF files are accepted');
      return;
    }
    setFile(f);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragging(false);
    acceptFile(e.dataTransfer.files[0]);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!file) { toast.error('Please select a PDF file'); return; }
    if (!fields.tenantId) { toast.error('Please select a tenant'); return; }
    if (!fields.basicRent) { toast.error('Basic rent is required'); return; }

    const formData = new FormData();
    formData.append('file', file);
    formData.append('tenantId', fields.tenantId);
    formData.append('receiptNo', fields.receiptNo);
    formData.append('basicRent', fields.basicRent);
    formData.append('charges', fields.charges || '0');
    formData.append('paymentDate', fields.paymentDate);
    formData.append('periodStart', fields.periodStart);
    formData.append('periodEnd', fields.periodEnd);
    formData.append('paymentType', fields.paymentType);
    formData.append('language', fields.language);
    formData.append('isSigned', fields.isSigned ? 'true' : 'false');
    formData.append('notes', fields.notes);

    setLoading(true);
    try {
      await receiptApi.import(formData);
      toast.success('Receipt imported successfully');
      if (tenantId) navigate(`/tenants/${tenantId}/receipts`);
      else navigate('/receipts');
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to import receipt');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="card max-w-2xl mx-auto">
      <h1 className="text-2xl font-bold text-gray-900 mb-6">Import Existing Receipt</h1>

      <form onSubmit={handleSubmit}>
        <div className="space-y-6">

          {/* File upload */}
          <div>
            <label className="form-label">Receipt PDF</label>
            {file ? (
              <div className="flex items-center gap-3 p-3 border border-gray-200 rounded-md bg-gray-50">
                <DocumentIcon className="h-8 w-8 text-red-500 flex-shrink-0" />
                <span className="text-sm text-gray-700 flex-1 truncate">{file.name}</span>
                <button
                  type="button"
                  onClick={() => { setFile(null); if (fileInputRef.current) fileInputRef.current.value = ''; }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <XMarkIcon className="h-5 w-5" />
                </button>
              </div>
            ) : (
              <div
                onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
                onDragLeave={() => setDragging(false)}
                onDrop={handleDrop}
                onClick={() => fileInputRef.current?.click()}
                className={`flex flex-col items-center justify-center border-2 border-dashed rounded-md p-8 cursor-pointer transition-colors ${
                  dragging ? 'border-primary-400 bg-primary-50' : 'border-gray-300 hover:border-gray-400 bg-gray-50'
                }`}
              >
                <ArrowUpTrayIcon className="h-8 w-8 text-gray-400 mb-2" />
                <p className="text-sm text-gray-600">
                  Drag and drop a PDF, or <span className="text-primary-600 font-medium">browse</span>
                </p>
                <p className="text-xs text-gray-400 mt-1">PDF files only</p>
              </div>
            )}
            <input
              ref={fileInputRef}
              type="file"
              accept=".pdf,application/pdf"
              className="hidden"
              onChange={(e) => acceptFile(e.target.files[0])}
            />
          </div>

          {/* Tenant */}
          <div>
            <label className="form-label">Tenant</label>
            <select
              name="tenantId"
              className="form-input"
              value={fields.tenantId}
              onChange={handleTenantChange}
              disabled={Boolean(tenantId)}
              required
            >
              {!tenantId && <option value="">Select a tenant</option>}
              {tenants
                .filter(t => !tenantId || t.id === parseInt(tenantId))
                .map(t => (
                  <option key={t.id} value={t.id}>
                    {t.firstName} {t.lastName}
                  </option>
                ))}
            </select>
          </div>

          {/* Receipt number */}
          <div>
            <label className="form-label">
              Receipt Number <span className="text-gray-400 font-normal">(leave blank to auto-generate)</span>
            </label>
            <input
              name="receiptNo"
              type="text"
              className="form-input"
              placeholder="e.g. 20240101-1-1-5"
              value={fields.receiptNo}
              onChange={handleField}
            />
          </div>

          {/* Amounts */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="form-label">Basic Rent</label>
              <input
                name="basicRent"
                type="number"
                step="0.01"
                min="0"
                className="form-input"
                placeholder={selectedTenant ? String(selectedTenant.basicRent) : '0.00'}
                value={fields.basicRent}
                onChange={handleField}
                required
              />
            </div>
            <div>
              <label className="form-label">Charges</label>
              <input
                name="charges"
                type="number"
                step="0.01"
                min="0"
                className="form-input"
                placeholder="0.00"
                value={fields.charges}
                onChange={handleField}
              />
            </div>
          </div>

          {fields.basicRent && (
            <p className="text-sm text-gray-600 -mt-2">
              Total: <strong>{(parseFloat(fields.basicRent || 0) + parseFloat(fields.charges || 0)).toFixed(2)}</strong>
              {selectedTenant?.currency ? ` ${selectedTenant.currency}` : ''}
            </p>
          )}

          {/* Payment date */}
          <div>
            <label className="form-label">Payment Date</label>
            <input
              name="paymentDate"
              type="date"
              className="form-input"
              value={fields.paymentDate}
              onChange={handleField}
              required
            />
          </div>

          {/* Rental period */}
          <div>
            <label className="form-label">Rental Period</label>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="form-label text-xs">Start Date</label>
                <input
                  name="periodStart"
                  type="date"
                  className="form-input"
                  value={fields.periodStart}
                  onChange={handleField}
                  required
                />
              </div>
              <div>
                <label className="form-label text-xs">End Date</label>
                <input
                  name="periodEnd"
                  type="date"
                  className="form-input"
                  value={fields.periodEnd}
                  onChange={handleField}
                  required
                />
              </div>
            </div>
          </div>

          {/* Payment method */}
          <div>
            <label className="form-label">Payment Method</label>
            <select name="paymentType" className="form-input" value={fields.paymentType} onChange={handleField}>
              <option>Bank Transfer</option>
              <option>Cash</option>
              <option>Check</option>
              <option>Credit Card</option>
              <option>Debit Card</option>
              <option>Online Payment</option>
              <option>Other</option>
            </select>
          </div>

          {/* Language */}
          <div>
            <label className="form-label">Language</label>
            <select name="language" className="form-input" value={fields.language} onChange={handleField}>
              <option value="fr">French</option>
              <option value="en">English</option>
            </select>
          </div>

          {/* Is signed */}
          <div className="flex items-center gap-3">
            <input
              id="isSigned"
              name="isSigned"
              type="checkbox"
              className="h-4 w-4 text-primary-600 border-gray-300 rounded"
              checked={fields.isSigned}
              onChange={handleField}
            />
            <label htmlFor="isSigned" className="text-sm font-medium text-gray-700">
              This receipt is already signed
            </label>
          </div>

          {/* Notes */}
          <div>
            <label className="form-label">Notes (Optional)</label>
            <textarea
              name="notes"
              rows="3"
              className="form-input"
              placeholder="Any additional information..."
              value={fields.notes}
              onChange={handleField}
            />
          </div>

          <div className="flex justify-end space-x-4 pt-4">
            <button
              type="button"
              className="btn-secondary"
              onClick={() => tenantId ? navigate(`/tenants/${tenantId}/receipts`) : navigate('/receipts')}
              disabled={loading}
            >
              Cancel
            </button>
            <button type="submit" className="btn-primary" disabled={loading}>
              {loading ? 'Importing...' : 'Import Receipt'}
            </button>
          </div>
        </div>
      </form>
    </div>
  );
};

export default ImportReceiptForm;
